// Arma la estructura genérica de reporte (title/subtitle/summary/sections) de Constructora.
// La usan tanto la pestaña "Reportes" del proyecto como Reportes → Constructora.

export const fmtMon = (n, moneda = "PYG") => {
  const v = Number(n) || 0;
  if (moneda === "PYG") return `₲ ${Math.round(v).toLocaleString("es-PY")}`;
  return `${moneda} ${v.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ESTADO_TAREA = { cumplida: "Cumplida", en_curso: "En curso", atrasada: "Atrasada", pendiente: "Pendiente" };

export function buildReporteConstructora(tipo, data) {
  const pr = data?.proyecto || {};
  const m = pr.moneda || "PYG";
  const f = (n) => fmtMon(n, m);
  const sub = `${pr.numero || ""} — ${pr.nombre || ""}`;

  if (tipo === "general") {
    const r = data.resumen || {};
    return {
      title: "Constructora — Estado general del proyecto",
      subtitle: sub,
      summary: [
        ["Monto del contrato", f(r.contrato)],
        ["Presupuesto (con IVA)", f(r.presupuesto_total)],
        ["Avance físico", `${r.avance_fisico_pct || 0}%`],
        ["Facturado", f(r.facturado)],
        ["Por facturar", f(r.por_facturar)],
        ["Cobrado (incl. anticipos disponibles)", f(r.cobrado)],
        ["Anticipos recibidos", f(r.anticipos_total)],
        ["Pendiente de cobro", f(r.pendiente_cobrar)],
        ["Plan de cobros vencido", f(r.plan_cobros_vencido)],
        ["Costo compras", f(r.costo_compras)],
        ["Costo gastos", f(r.costo_gastos)],
        ["Costo personal", f(r.costo_personal)],
        ["Costo total", f(r.costo_total)],
        ["Margen actual (cobrado − costos)", f(r.margen)],
        ["Margen proyectado (contrato − costos)", f(r.margen_proyectado)],
      ],
      sections: [
        {
          title: "Cobros (facturas y anticipos)",
          columns: ["Fecha", "Tipo", "Referencia", "Concepto", "Monto", "Cobrado", "Estado"],
          rows: (data.cobros || []).map((c) => [c.fecha || "-", c.tipo, c.referencia || "-", c.concepto || "-", f(c.monto), f(c.cobrado), c.estado || "-"]),
        },
        {
          title: "Plan de cobros pactado",
          columns: ["Concepto", "Fecha prevista", "Monto", "Estado", "Fecha de cobro"],
          rows: (data.plan_cobros || []).map((c) => [c.concepto, c.fecha_prevista || "-", f(c.monto), c.estado === "cobrado" ? "Cobrado" : "Pendiente", c.fecha_cobro || "-"]),
        },
      ],
    };
  }

  if (tipo === "costos") {
    const t = data.totales || {};
    return {
      title: "Constructora — Costos por grupo",
      subtitle: sub,
      summary: [["Compras", f(t.compras)], ["Gastos", f(t.gastos)], ["Personal", f(t.personal)], ["Total", f(t.total)]],
      sections: [
        {
          title: "Costos por grupo",
          columns: ["Grupo", "Compras", "Gastos", "Personal", "Total"],
          rows: (data.por_grupo || []).map((g) => [g.grupo, f(g.compras), f(g.gastos), f(g.personal), f(g.total)]),
        },
        {
          title: "Personal — total pagado por empleado",
          columns: ["Empleado", "Pagos", "Total"],
          rows: (data.personal || []).map((e) => [e.empleado || "-", e.pagos, f(e.total)]),
        },
        {
          title: "Detalle de compras",
          columns: ["Fecha", "Proveedor", "Factura", "Grupo", "Monto"],
          rows: (data.compras || []).map((c) => [c.fecha || "-", c.proveedor || "-", c.factura || "-", c.grupo, f(c.monto)]),
        },
      ],
    };
  }

  if (tipo === "cronograma") {
    const r = data.resumen || {};
    return {
      title: "Constructora — Cronograma y atrasos",
      subtitle: sub,
      summary: [
        ["Tareas", r.tareas], ["Cumplidas", r.cumplidas], ["En curso", r.en_curso], ["Atrasadas", r.atrasadas], ["Pendientes", r.pendientes],
        ["Avance promedio", `${r.avance_promedio_pct || 0}%`],
        ["Fin según línea base", r.fin_linea_base || "-"], ["Fin vigente", r.fin_vigente || "-"],
        ["Corrimiento total (días)", r.corrimiento_total_dias],
        ["Días de lluvia", r.dias_lluvia], ["Atraso sin contar lluvia (días)", r.atraso_sin_lluvia_dias],
        ["Reprogramaciones", r.reprogramaciones],
      ],
      sections: [
        {
          title: "Tareas",
          columns: ["Código", "Tarea", "Plan inicio", "Plan fin", "Línea base fin", "Desvío (días)", "Avance", "Fin real", "Estado", "Atraso (días)"],
          rows: (data.tareas || []).map((t) => [t.codigo || "-", (t.nivel ? "   · " : "") + t.nombre, t.inicio || "-", t.fin || "-", t.base_fin || "-", t.desvio_dias || 0, `${t.avance_pct || 0}%`, t.fin_real || "-", ESTADO_TAREA[t.estado] || t.estado, t.atraso_dias || 0]),
        },
        {
          title: "Reprogramaciones",
          columns: ["Fecha", "Usuario", "Tarea", "Antes", "Ahora", "Motivo"],
          rows: (data.reprogramaciones || []).map((x) => [(x.fecha || "").slice(0, 10), x.usuario || "-", x.tarea || "-", `${x.de_inicio || "-"} → ${x.de_fin || "-"}`, `${x.a_inicio || "-"} → ${x.a_fin || "-"}`, x.motivo || "-"]),
        },
        {
          title: "Días de lluvia / clima",
          columns: ["Fecha", "Tipo", "Días", "Notas"],
          rows: (data.clima || []).map((c) => [c.fecha, c.tipo, c.dias, c.notas || "-"]),
        },
      ],
    };
  }

  // equipos
  return {
    title: "Constructora — Equipos y maquinarias",
    subtitle: sub,
    summary: [["Equipos asignados", (data.equipos || []).length], ["Días de uso (total)", data.total_dias || 0]],
    sections: [{
      title: "Uso de equipos",
      columns: ["Equipo", "Categoría", "Desde", "Hasta", "Días", "Compartido con otro proyecto", "Notas"],
      rows: (data.equipos || []).map((e) => [e.activo_nombre, e.categoria || "-", e.desde, e.hasta || "En uso", e.dias, e.compartido_con || "No", e.notas || "-"]),
    }],
  };
}

export function imprimirReporte(report) {
  const w = window.open("", "_blank");
  if (!w) return;
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
  const sum = (report.summary || []).map(([l, v]) => `<tr><td>${esc(l)}</td><td style="text-align:right"><b>${esc(v)}</b></td></tr>`).join("");
  const secs = (report.sections || []).map((s) => `
    <h3>${esc(s.title)}</h3>
    <table><thead><tr>${s.columns.map((c) => `<th>${esc(c)}</th>`).join("")}</tr></thead>
    <tbody>${(s.rows || []).length ? s.rows.map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`).join("") : `<tr><td colspan="${s.columns.length}" style="text-align:center;color:#888">Sin datos</td></tr>`}</tbody></table>`).join("");
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${esc(report.title)}</title>
  <style>body{font-family:Arial,sans-serif;font-size:11px;padding:20px;color:#111}h1{font-size:16px;margin:0}h3{font-size:12px;margin:16px 0 4px}
  table{width:100%;border-collapse:collapse;margin-bottom:8px}td,th{border:1px solid #ccc;padding:4px 6px;text-align:left}th{background:#f1f5f9;font-size:10px}
  .sum{width:auto;min-width:360px}@media print{.np{display:none}}</style></head><body>
  <div class="np" style="text-align:right"><button onclick="window.print()">Imprimir</button></div>
  <h1>${esc(report.title)}</h1><div style="color:#555;margin-bottom:8px">${esc(report.subtitle)}</div>
  <table class="sum"><tbody>${sum}</tbody></table>${secs}</body></html>`);
  w.document.close();
}
